import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCircleInfo, faFilterCircleXmark, faWallet } from '@fortawesome/free-solid-svg-icons';
import { pln } from './format';
import { useTranslation, Trans } from 'react-i18next';

// Pasek pokazujący, jaka część produktów przeszła dany etap/warunek.
const Bar = ({ kept, base }) => {
  const pct = base > 0 ? Math.round((kept / base) * 100) : 0;
  return (
    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-1">
      <div
        className={`h-full rounded-full transition-all ${kept === 0 ? 'bg-rose-400' : pct < 15 ? 'bg-amber-400' : 'bg-blue-500'}`}
        style={{ width: `${Math.max(kept > 0 ? 3 : 0, pct)}%` }}
      />
    </div>
  );
};

// Brak wyników (§18): DIAGNOZA (dlaczego), lejek odrzuceń, wpływ pojedynczych
// warunków i przyciski relaksacji. Front niczego nie liczy — wszystko z API.
// onRelax(relaxation) / onRaiseBudget(kwota) — obsługa w stronie.
const NoResults = ({ data, onRelax, onRaiseBudget, kosztyWlasneZl = 0 }) => {
  const { t } = useTranslation('asystent');
  const funnel = data.funnel || [];
  const relaxations = data.relaxations || [];
  const diagnosis = data.diagnosis_pl || [];
  const impact = data.filter_impact || [];
  /**
   * Kwota z silnika obejmuje TYLKO produkty i dostawę — konstrukcja i montaż są
   * naszym szacunkiem, doliczanym po stronie frontu. Pole budżetu jest natomiast
   * budżetem ŁĄCZNYM, więc podpowiadanie tu surowej liczby z silnika prowadziło
   * donikąd: użytkownik klikał „Ustaw ten budżet", dostawał dokładnie tę kwotę
   * i dalej nie widział żadnego zestawu, bo po odjęciu naszych pozycji na sprzęt
   * zostawało za mało. Podajemy więc od razu kwotę łączną.
   */
  const cheapest = data.cheapest_total_pln != null
    ? data.cheapest_total_pln + (kosztyWlasneZl || 0)
    : null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-3">
        {data.message_pl || t('brak_wynikow.naglowek')}
      </h2>

      {/* DIAGNOZA — najważniejsze: dlaczego nie wyszło */}
      {diagnosis.length > 0 && (
        <ul className="mb-5 space-y-2">
          {diagnosis.map((d, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-gray-800 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5">
              <FontAwesomeIcon icon={faCircleInfo} className="text-amber-500 mt-0.5 shrink-0" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Bez kwoty z silnika nie zmyślamy liczby — mówimy tylko, co jest nie tak.
          Zdanie „podnieś budżet do X" z wyssaną wartością byłoby gorsze niż brak
          podpowiedzi: użytkownik ustawiłby X i znowu nic nie zobaczył. */}
      {/* `buget`: ten sam warunek w tekście z backendu przetłumaczonym na rumuński. */}
      {cheapest == null && data.limiting_constraints_pl?.some((c) => /budżet|buget/i.test(c)) && (
        <p className="mb-5 rounded-xl border-2 border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t('brak_wynikow.budzet_za_niski')}
        </p>
      )}

      {/* Najtańszy możliwy zestaw — jedno kliknięcie, żeby go zobaczyć */}
      {cheapest != null && onRaiseBudget && (
        <button
          type="button"
          onClick={() => onRaiseBudget(Math.ceil(cheapest / 100) * 100)}
          className="w-full mb-5 flex items-center justify-between gap-3 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <span className="flex items-center gap-2.5 min-w-0">
            <FontAwesomeIcon icon={faWallet} className="text-blue-600 shrink-0" />
            <span className="text-sm text-blue-900">
              <Trans t={t} i18nKey="brak_wynikow.najtanszy" values={{ kwota: pln(cheapest) }}
                components={{ b: <strong /> }} />
              {kosztyWlasneZl > 0 && (
                <span className="block text-[11px] text-blue-700/80">
                  {t('brak_wynikow.z_konstrukcja')}
                </span>
              )}
            </span>
          </span>
          <span className="text-sm font-semibold text-blue-700 shrink-0 flex items-center gap-1.5">
            {t('brak_wynikow.ustaw_budzet')} <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </span>
        </button>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2.5">{t('brak_wynikow.gdzie_odpadly')}</p>
          <div className="space-y-2.5">
            {funnel.map((step, i) => (
              <div key={i}>
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="text-gray-700 truncate">{step.stage}</span>
                  <span className={`shrink-0 tabular-nums ${step.out === 0 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                    {step.in} → {step.out}
                  </span>
                </div>
                <Bar kept={step.out} base={step.in} />
              </div>
            ))}
          </div>

          {impact.length > 0 && (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-5 mb-2.5 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faFilterCircleXmark} className="text-gray-300" />
                {t('brak_wynikow.kazdy_warunek')}
              </p>
              <div className="space-y-2.5">
                {impact.slice(0, 6).map((f, i) => (
                  <div key={i}>
                    <div className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="text-gray-700 truncate">
                        <span className="text-gray-400 text-xs">{f.stage}: </span>{f.label_pl}
                      </span>
                      <span className={`shrink-0 tabular-nums text-xs ${f.kept === 0 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                        {t('brak_wynikow.z', { kept: f.kept, base: f.base })}
                      </span>
                    </div>
                    <Bar kept={f.kept} base={f.base} />
                    {f.missing > 0 && (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {t('brak_wynikow.bez_danej', { n: f.missing })}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2.5">{t('brak_wynikow.co_zmienic')}</p>
          {relaxations.length > 0 ? (
            <div className="space-y-2">
              {relaxations.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onRelax(r)}
                  className="w-full text-left border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg px-3 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <span className="text-sm font-medium text-blue-800 flex items-center gap-1.5">
                    {r.label_pl} <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                  </span>
                  {r.variants_found != null && (
                    <span className="text-xs text-blue-600">
                      {t('brak_wynikow.znalezlibysmy', { count: r.variants_found })}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              {t('brak_wynikow.zadna_zmiana')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoResults;
